/**
 * @file LandingPage.js
 * @author Stanley Chen
 * @description Main landing page component with a full-screen video background,
 * restaurant logo, and call-to-action buttons for ordering and viewing the menu
 */

import React, { useRef, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './LandingPage.css';
import Scroll from './Scroll.js';
import { Link } from 'react-router-dom'; 
import OutboundLink from '../OutboundLink';
// src/components/LandingPage/LandingPage.js

import { isMobileDevice, getVideoSource, getAnimationConfig } from '../../utils/DeviceDetection';

const LandingPage = () => {
  const videoRef = useRef(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const animationConfig = getAnimationConfig();
  const videoSource = getVideoSource();

  useEffect(() => {
    const video = videoRef.current;

    const handleLoadedData = () => {
      setIsVideoLoaded(true);
    };

    if (video) {
      video.addEventListener('loadeddata', handleLoadedData);
    }

    return () => {
      if (video) {
        video.removeEventListener('loadeddata', handleLoadedData);
      }
    };
  }, []);

  return (
    <section className="landing-page" id="home">
      <div className="video-container">
        <AnimatePresence>
          {!isVideoLoaded && (
            <motion.div
              className="video-placeholder"
              initial={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.1 }}
            />
          )}
        </AnimatePresence>


          <video 
            ref={videoRef}
            autoPlay 
            loop 
            muted 
            playsInline 
            className={`landing-page-video ${isVideoLoaded ? 'loaded' : ''}`}
            preload="auto"
          >
            <source src={videoSource} type="video/mp4" />
          </video>

      </div>
      
      <div className="landing-page-overlay" />

      <div className="landing-page-content">
        <motion.div
          className="logo-container"
          {...animationConfig}
        >
          <img 
            src="/images/kiku-logo-large.png" 
            alt="Kiku Japanese Restaurant" 
            className="landing-page-logo" 
            loading={isMobileDevice() ? "eager" : "lazy"}
          />
        </motion.div>

        <motion.div 
          className="button-container"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <OutboundLink 
            href="https://kikude.kwickmenu.com/"
            label="Online Ordering - Pickup (Hero)"
          >
            <motion.button
              className="cta-button primary"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              ORDER NOW
            </motion.button>
          </OutboundLink>

          <Link to="/menu">
            <motion.button
              className="cta-button secondary"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              VIEW MENU
            </motion.button>
          </Link>

        </motion.div>
      </div>

      <Scroll />
    </section>
  );
};

export default LandingPage;
